"use client";

import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"

/**
 * 
 * @param {Object} props
 * @param {String} props.name Form input name
 * @param {String} [props.ariaLabel] Form input aria-label
 * @param {import("react").ReactNode} [props.errorMessage] Validation error message
 * @param {String | undefined} [props.ariaErrormessageId] Error message id for external elements
 * @param {Boolean} [props.typeRadio] Converts to radio button
 * @param {String} [props.radioGroupName] Groups radio buttons under a grou name
 * @param {Function | undefined} [props.onValidate] Runs method on validation
 * @param {React.MouseEventHandler<HTMLInputElement> | undefined} [props.onClick] Runs method on click
 * @param {Function | undefined} [props.onChange] Runs method on change
 * @param {Boolean} [props.required] Required flag
 * @param {Boolean} [props.forceShowAsterisc] Shows asterisc on error flag
 * @param {Boolean} [props.omitBlurValidate] Omits auto validation on blur, useful when input is not required
 * @param {String | undefined} [props.customClass] Add custom class names
 * @param {React.FocusEventHandler<HTMLLabelElement>} [props.onBlur] Runs method on blue (focus out)
 * @param {Boolean} [props.disabled] Disable element
 * @param {import("react").ReactNode} props.children Checkbox label/description
 * @param {React.InputHTMLAttributes<HTMLInputElement>} [props.rest] Rest...
 * @param {*} ref
 * @returns {import("react").JSX.Element} The rendered input component.
 */
function FormInputCheckbox({
  name,
  ariaLabel,
  errorMessage = <>This field is required.</>,
  ariaErrormessageId = undefined,
  typeRadio = false,
  radioGroupName = undefined,
  onValidate = undefined,
  onClick = undefined,
  onChange = undefined,
  required = false,
  forceShowAsterisc = false,
  omitBlurValidate = false,
  customClass = undefined,
  onBlur = undefined,
  disabled = false,
  children,
  ...rest
}, ref
) {
  const inputRef = useRef(null);
  const [valid, setValid] = useState(true);
  const containerClass = useMemo(() => {
    let className = 'form-input-checkbox';

    if (!valid) {
      className = `${className} form-input-checkbox--invalid`;
    }

    if (customClass) {
      className = `${className} ${customClass}`;
    }

    return className;
  }, [valid, customClass]);

  function isChecked() {
    return inputRef.current?.checked;
  }

  function handleValidate(e) {
    if (
      'readOnly' in rest && rest.readOnly ||
      'disabled' in rest && rest.disabled ||
      e?.target?.tagName === 'A'
    ) {
      return;
    }

    if (e && e.type === 'blur' && omitBlurValidate) {
      if (onBlur) {
        onBlur(e);
      }
      return valid;
    }

    const isValid = (required && inputRef.current?.checked) || !required
    setValid(isValid);

    if (onValidate) {
      onValidate({type: e.type, name, valid: isValid, checked: isChecked()});
    }

    if (onBlur) {
      onBlur(e);
    }

    return isValid;
  }

  function testValidation() {
    const isValid = (required && inputRef.current?.checked) || !required
    return isValid;
  }


  useImperativeHandle(
    ref,
    () => ({
      validate() {
        return handleValidate();
      },
      testValidation() {
        return testValidation();
      },
      setValid(newValue) {
        setValid(newValue);
      },
      get isValid() {
        return valid;
      },
      get checked() {
        return isChecked();
      },
      setCheck(value) {
        inputRef.current.checked = value;
      },
      reset() {
        setValid(true);
        if (inputRef.current) {
          inputRef.current.checked = false;
        }
      },
      get name() {
        return name;
      },
      get type() {
        return typeRadio ? 'radio' : 'checkbox';
      },
      setFocus() {
        if (window.innerWidth < 699) {
          /** @type {HTMLElement} */(document.activeElement)?.blur();
          const inputElement = inputRef.current;
          inputElement.blur();

          inputRef.current?.focus({ focusVisible: true });
          inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });console.log('scrollIntoView');
        } else {
          inputRef.current?.focus({ focusVisible: true });
        }
      },
  }));

  const errormessageId = useMemo(() => {
    let messageId = !valid ? `form_input_${name}_error_message` : undefined;

    if (ariaErrormessageId) {
      messageId = ariaErrormessageId;
    }

    return messageId;
  }, [valid, name, ariaErrormessageId]);

  return (
    <div id={`form_input_${name}`} className={containerClass}>
      <label
        id={`form_input_${name}_label`}
        aria-label={ariaLabel}
        onBlur={handleValidate}
      >
        <input
          ref={inputRef}
          type={typeRadio ? 'radio' : 'checkbox'}
          required={required}
          onChange={(e) => {
            handleValidate(e);
            onChange && onChange(e);
          }}
          aria-invalid={!valid}
          aria-labelledby={`form_input_${name}_label`}
          aria-errormessage={errormessageId}
          name={radioGroupName}
          onClick={(event) => onClick && onClick(event)}
          tabIndex={0}
          disabled={disabled}
          {...rest}
        />
        <span className={typeRadio ? 'custom-radio-button' : "custom-checkbox"}></span>
        <span>{(required && !valid) || (forceShowAsterisc && !valid) || required ? <span className="hang">*</span> : '' }{children}</span>
      </label>
      {!valid && errorMessage && <div
        id={`form_input_${name}_error_message`}
        className="invalid-feedback"
      >
        {errorMessage}
      </div>}
    </div>
  );
};

export default forwardRef(FormInputCheckbox);